import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDateTime, formatMoney } from '../../lib/api';
import type { Agency, Contravention, ContraventionStatus, ParkingZone } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { useCursorList } from '../../lib/useCursorList';
import { EmptyState, ErrorNote, Field, PageHeader, Pagination, Spinner, StatusBadge } from '../../ui/components';

const STATUSES: ContraventionStatus[] = [
  'ISSUED',
  'PAID',
  'UNDER_APPEAL',
  'APPEAL_UPHELD',
  'APPEAL_REJECTED',
  'ESCALATED',
  'REFERRED_TO_COURT',
  'CANCELLED',
  'WRITTEN_OFF',
];
const AGENCIES: Agency[] = ['SLTA', 'POLICE'];

export function ContraventionsList() {
  const { data: zonesPage } = useApiQuery<{ items: ParkingZone[] }>('/api/v1/parking/zones');
  const zones = zonesPage?.items ?? [];
  const zoneName = useMemo(() => {
    const map = new Map(zones.map((z) => [z.id, `${z.code} — ${z.name}`]));
    return (id: string) => map.get(id) ?? id;
  }, [zones]);

  const [status, setStatus] = useState('');
  const [agency, setAgency] = useState('');
  const [zoneId, setZoneId] = useState('');
  const [officerId, setOfficerId] = useState('');
  const [q, setQ] = useState('');

  const basePath = useMemo(() => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (agency) params.set('agency', agency);
    if (zoneId) params.set('zoneId', zoneId);
    if (officerId.trim()) params.set('officerId', officerId.trim());
    if (q.trim()) params.set('q', q.trim());
    const qs = params.toString();
    return `/api/v1/parking/contraventions${qs ? `?${qs}` : ''}`;
  }, [status, agency, zoneId, officerId, q]);

  const listQ = useCursorList<Contravention>(basePath);

  return (
    <div>
      <PageHeader title="Contraventions" subtitle="Notices issued across every zone and agency — search by notice number or registration number." />

      <div className="mb-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
        <Field label="Status" className="!mb-0">
          <select className="field-input" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Agency" className="!mb-0">
          <select className="field-input" value={agency} onChange={(e) => setAgency(e.target.value)}>
            <option value="">All agencies</option>
            {AGENCIES.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Zone" className="!mb-0">
          <select className="field-input" value={zoneId} onChange={(e) => setZoneId(e.target.value)}>
            <option value="">All zones</option>
            {zones.map((z) => (
              <option key={z.id} value={z.id}>
                {z.code}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Officer ID" className="!mb-0" hint="Raw EnforcementOfficer id — no lookup endpoint to search by badge.">
          <input className="field-input" value={officerId} onChange={(e) => setOfficerId(e.target.value)} placeholder="cuid…" />
        </Field>
        <Field label="Search" className="!mb-0">
          <input className="field-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Notice no. or reg. no." />
        </Field>
      </div>

      {listQ.error ? (
        <div className="mb-3">
          <ErrorNote message={listQ.error} />
        </div>
      ) : null}

      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : listQ.items.length === 0 ? (
          <EmptyState title="No contraventions match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Notice</th>
                <th className="table-head">Registration</th>
                <th className="table-head">Zone</th>
                <th className="table-head">Agency</th>
                <th className="table-head">Issued</th>
                <th className="table-head">Amount</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.items.map((c) => (
                <tr key={c.id} className="hover:bg-slate-50">
                  <td className="table-cell font-mono text-xs">
                    <Link className="font-semibold text-brand-600 hover:underline" to={`/contraventions/${c.id}`}>
                      {c.noticeNumber}
                    </Link>
                    {c.flaggedForReview ? <span className="ml-1.5 text-amber-500" title="Flagged for supervisor review">⚑</span> : null}
                  </td>
                  <td className="table-cell font-mono text-xs text-slate-600">{c.registrationNumberAsObserved}</td>
                  <td className="table-cell text-xs text-slate-500">{zoneName(c.zoneId)}</td>
                  <td className="table-cell text-xs">{c.agency}</td>
                  <td className="table-cell text-xs">{formatDateTime(c.issuedAt)}</td>
                  <td className="table-cell text-xs tabular-nums">{formatMoney(c.escalatedAmountCents ?? c.amountCents)}</td>
                  <td className="table-cell">
                    <StatusBadge status={c.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <Pagination hasMore={listQ.hasMore} onMore={listQ.loadMore} loading={listQ.loadingMore} />
      </div>
    </div>
  );
}
